import React, { FunctionComponent, PropsWithChildren, useEffect, useMemo, useState } from "react";
import { styled, ProjectTitle, media, skeletonShimmer } from "@iventis/styles";
import { Theme } from "@emotion/react";
import { useHistory } from "@iventis/subscriptions";

import { Content } from "@iventis/translations";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { NodeType } from "@iventis/domain-model/model/nodeType";
import { Button } from "@mui/material";
import { Node } from "@iventis/domain-model/model/node";
import { dataTestIds } from "@iventis/testing";
import { useProject } from "@iventis/permissions/src/use-project";
import { IventisErrorBoundaryWithToast } from "@iventis/error-boundaries";
import { toast } from "@iventis/toasts/src/toast";
import { StaticLibraryId } from "@iventis/plans/src/plan-library/mapping-library.helpers";
import { CreateMapComponent } from "@iventis/plans/src/plan-library/create-map";
import { getSpatialLibraries } from "@iventis/plans/src/services/libraries";
import PageWithAppBar from "./page-with-appbar";
import { DashboardWidget } from "./dashboard-widget";
import { CoverImageComponent } from "./dashboard-image";
import { DashboardLibraryComponent } from "./dashboard-library";
import { DashboardSkeletonErrorBoundary } from "./dashboard-skeleton";

const SpatialDashboardContent: FunctionComponent<PropsWithChildren> = ({ children }) => {
    const project = useProject();
    const history = useHistory();

    const translate = useIventisTranslate();

    const [libraries, setLibraries] = useState<Node[]>(null);
    const [modalOpen, setModalOpen] = useState<boolean>(false);

    useEffect(() => {
        const abortController = new AbortController();
        getSpatialLibraries(abortController.signal).then((res) => setLibraries(res));
        return () => {
            abortController.abort();
        };
    }, []);

    const defaultLibrary = useMemo(() => libraries?.find((lib) => lib.type === NodeType.PersonalMappingLibrary) ?? libraries?.find((lib) => lib.type === NodeType.MappingLibrary), [
        libraries,
    ]);

    return (
        <>
            <Container>
                <IventisErrorBoundaryWithToast toast={toast}>
                    <StyledBannerContainer>
                        <IventisErrorBoundaryWithToast toast={toast}>
                            <CoverImageComponent />
                        </IventisErrorBoundaryWithToast>
                        <IventisErrorBoundaryWithToast toast={toast}>
                            <StyledProjectInfoContainer>
                                {project?.name ? (
                                    <ProjectTitle style={{ marginBottom: "9px", width: "300px" }}>{project?.name}</ProjectTitle>
                                ) : (
                                    <SkeletonPlaceholder>
                                        <ProjectTitle style={{ marginBottom: "9px", width: "300px" }}>{project?.name}</ProjectTitle>
                                    </SkeletonPlaceholder>
                                )}
                            </StyledProjectInfoContainer>
                        </IventisErrorBoundaryWithToast>
                        <IventisErrorBoundaryWithToast toast={toast}>
                            <StyledButton
                                data-testid={dataTestIds.dashboard.createMapButton}
                                variant="contained"
                                color="primary"
                                onClick={() => setModalOpen(true)}
                                disabled={defaultLibrary == null}
                            >
                                {translate(Content.map.create_map.create_a_new_map)}
                            </StyledButton>
                        </IventisErrorBoundaryWithToast>
                    </StyledBannerContainer>
                </IventisErrorBoundaryWithToast>
                <StyledContent>
                    <DashboardWidget title={translate(Content.library.static_library_names.recently_viewed)} onClick={() => history.push("/spatial-planner/recently-viewed")}>
                        <DashboardLibraryComponent
                            libraryType={StaticLibraryId.RecentlyViewed}
                            onClick={() => history.push("/spatial-planner/recently-viewed")}
                            onClickEmpty={() => setModalOpen(true)}
                        />
                    </DashboardWidget>
                    <DashboardWidget title={translate(Content.library.static_library_names.favourites)} onClick={() => history.push("/spatial-planner/favourites")}>
                        <DashboardLibraryComponent
                            libraryType={StaticLibraryId.Favourites}
                            onClick={() => history.push("/spatial-planner/favourites")}
                            onClickEmpty={() => history.push("/spatial-planner")}
                        />
                    </DashboardWidget>
                </StyledContent>
                <CreateMapComponent open={modalOpen} close={() => setModalOpen(false)} tree={defaultLibrary} parentId={defaultLibrary?.id} />
            </Container>
            {children}
        </>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    overflow-y: auto;
`;

const SkeletonPlaceholder = styled.span`
    ${skeletonShimmer({ sequence: 0 })};
    transform: scale(0.9);
    overflow: hidden;
`;

const StyledContent = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 50%);
    flex-grow: 1;

    ${media.extraSmall} {
        grid-template-columns: auto;
    }
`;

const StyledProjectInfoContainer = styled.div`
    position: absolute;
    left: 24px;
    bottom: 24px;
    color: ${({ theme }: { theme: Theme }) => theme.typographyColors.blank};
    overflow: hidden;
`;

const StyledButton = styled(Button)`
    position: absolute;
    right: 24px;
    bottom: 24px;
`;

const StyledBannerContainer = styled.div`
    flex: 2 1 495px;
    position: relative;
    overflow: hidden;
    min-height: 190px;
`;

const SpatialDashboard: FunctionComponent = () => (
    <PageWithAppBar>
        <DashboardSkeletonErrorBoundary>
            <SpatialDashboardContent />
        </DashboardSkeletonErrorBoundary>
    </PageWithAppBar>
);

export default SpatialDashboard;
